/**
 * CalloutNodeView - コールアウトの表示。左のアイコンを押すとアイコン・色の変更と解除ができる。
 */

import type { NodeViewProps } from '@tiptap/react'
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react'
import { useState } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

import { CALLOUT_COLORS, CALLOUT_ICONS, DEFAULT_CALLOUT_ICON, toCalloutColor } from './callout-options'

import './callout-node.scss'

export const CalloutNodeView = ({ node, editor, getPos, updateAttributes }: NodeViewProps) => {
  const [open, setOpen] = useState(false)
  const icon = String(node.attrs.icon || DEFAULT_CALLOUT_ICON)
  const color = toCalloutColor(node.attrs.color)

  const handleUnwrap = () => {
    setOpen(false)
    const pos = getPos()
    if (typeof pos !== 'number') return
    editor.chain().focus().setNodeSelection(pos).unsetCallout().run()
  }

  return (
    <NodeViewWrapper className="callout-node" data-color={color}>
      <div className="callout-node__icon" contentEditable={false}>
        {editor.isEditable ? (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <button type="button" className="callout-node__icon-button" aria-label="コールアウトのアイコンと色を変更">
                {icon}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 space-y-3 p-3" onOpenAutoFocus={(e) => e.preventDefault()}>
              <div className="grid grid-cols-6 gap-1">
                {CALLOUT_ICONS.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    aria-pressed={emoji === icon}
                    onClick={() => updateAttributes({ icon: emoji })}
                    className={`flex size-9 items-center justify-center rounded-md text-xl hover:bg-accent ${
                      emoji === icon ? 'bg-accent' : ''
                    }`}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-9 gap-1">
                {CALLOUT_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    title={c.label}
                    aria-label={c.label}
                    aria-pressed={c.value === color}
                    onClick={() => updateAttributes({ color: c.value })}
                    className={`callout-node__swatch size-6 rounded-full border ${
                      c.value === color ? 'ring-2 ring-primary ring-offset-1' : ''
                    }`}
                    data-color={c.value}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={handleUnwrap}
                className="w-full rounded-md px-2 py-1.5 text-left text-sm text-muted-foreground hover:bg-accent"
              >
                コールアウトを解除
              </button>
            </PopoverContent>
          </Popover>
        ) : (
          <span className="callout-node__icon-button">{icon}</span>
        )}
      </div>
      <NodeViewContent className="callout-node__content" />
    </NodeViewWrapper>
  )
}
